/**
 * Site Inspection entity DTOs.
 *
 * Mirrors Prisma `SiteInspection` + `SiteInspectionItem` + `CorrectiveAction`
 * (prisma/schema.prisma:2318+). A site inspection is a checklist of items
 * (PASS/FAIL/OBSERVATION/NOT_APPLICABLE) plus corrective actions. Web parity
 * with `app/api/projects/[projectId]/inspections/*`.
 *
 * The mobile mirror at `/api/mobile/v1/projects/:projectId/inspections/*`
 * consumes these via `safeParseOrWarn`. Mobile is owner-scoped (the project's
 * owner), like the sibling diary/incidents routes.
 */
import { z } from "zod";

export const SiteInspectionStatusSchema = z.enum([
  "DRAFT",
  "OPEN",
  "COMPLETE",
  "ARCHIVED",
]);
export type SiteInspectionStatus = z.infer<typeof SiteInspectionStatusSchema>;

/**
 * Plant & Equipment extension: GENERAL inspections predate plant support and
 * carry null plant context. All new plant fields are additive with defaults
 * so older serializers/clients keep parsing.
 */
export const SiteInspectionKindSchema = z.enum([
  "GENERAL",
  "PLANT_PRE_USE",
  "PLANT_WEEKLY",
  "PLANT_POST_REPAIR",
  "PLANT_OTHER",
]);
export type SiteInspectionKind = z.infer<typeof SiteInspectionKindSchema>;

export const SiteInspectionItemResultSchema = z.enum([
  "PASS",
  "FAIL",
  "OBSERVATION",
  "NOT_APPLICABLE",
]);
export type SiteInspectionItemResult = z.infer<
  typeof SiteInspectionItemResultSchema
>;

export const CorrectiveActionStatusSchema = z.enum([
  "OPEN",
  "IN_PROGRESS",
  "DONE",
  "CANCELLED",
]);
export type CorrectiveActionStatus = z.infer<
  typeof CorrectiveActionStatusSchema
>;

export const SiteInspectionItemSchema = z.object({
  id: z.string(),
  prompt: z.string(),
  result: SiteInspectionItemResultSchema,
  notes: z.string().nullable(),
  photoUrls: z.array(z.string()).default([]),
  safetyCritical: z.boolean().default(false),
  createdAt: z.string().datetime(),
});
export type SiteInspectionItem = z.infer<typeof SiteInspectionItemSchema>;

export const CorrectiveActionSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: CorrectiveActionStatusSchema,
    assignedToPortalUserId: z.string().nullable().default(null),
    assignedToName: z.string().nullable(),
    dueAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    evidenceNotes: z.string().nullable(),
    photoUrls: z.array(z.string()).default([]),
    safetyCritical: z.boolean().default(false),
    plantAssetId: z.string().nullable().default(null),
    projectPlantAssignmentId: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
    // Row version, carried since #2369: an edit claim (`expectedUpdatedAt` on
    // the update request) is only meaningful if the client can load the
    // version it last saw. Additive on a strict wire leaf — installed builds
    // fail the strict parse and fall back to raw via safeParseOrWarn, and a
    // client that never sees it simply sends no claim (last-writer-wins,
    // today's behaviour).
    updatedAt: z.string().datetime().optional(),
  });
export type CorrectiveAction = z.infer<typeof CorrectiveActionSchema>;

export const SiteInspectionSchema = z.object({
    id: z.string(),
    title: z.string(),
    location: z.string().nullable(),
    inspectedAt: z.string().datetime(),
    status: SiteInspectionStatusSchema,
    notes: z.string().nullable(),
    completedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    // Row version, carried since #2369 so an edit screen can echo it back as
    // `expectedUpdatedAt` (optional optimistic-concurrency claim on the update
    // request). Additive on a strict wire leaf — installed builds fail the
    // strict parse and fall back to raw via safeParseOrWarn; a client that
    // never sees it sends no claim and edits behave exactly as before.
    updatedAt: z.string().datetime().optional(),
    items: z.array(SiteInspectionItemSchema),
    actions: z.array(CorrectiveActionSchema),
    kind: SiteInspectionKindSchema.default("GENERAL"),
    plantAssetId: z.string().nullable().default(null),
    projectPlantAssignmentId: z.string().nullable().default(null),
    plantRequirementId: z.string().nullable().default(null),
    templateKey: z.string().nullable().default(null),
  });
export type SiteInspection = z.infer<typeof SiteInspectionSchema>;

// ─── #2411 server-boundary variants ─────────────────────────────────────────
// The mobile app parses the permissive bases above; the inspection routes'
// serialisers parse through these Strict forms.

export const SiteInspectionItemStrict = SiteInspectionItemSchema.strict();
export const CorrectiveActionStrict = CorrectiveActionSchema.strict();

export const SiteInspectionStrict = SiteInspectionSchema.extend({
  items: z.array(SiteInspectionItemStrict),
  actions: z.array(CorrectiveActionStrict),
}).strict();

// ─── Requests ────────────────────────────────────────────────────────────────

export const InspectionItemInput = z
  .object({
    prompt: z.string().min(1).max(240),
    result: SiteInspectionItemResultSchema.default("OBSERVATION"),
    notes: z.string().max(2000).nullable().optional(),
    photoUrls: z.array(z.string().max(2048)).max(10).default([]),
    safetyCritical: z.boolean().default(false),
  })
  .strict();

export const CorrectiveActionCreateRequest = z
  .object({
    title: z.string().min(1).max(180),
    description: z.string().max(2000).nullable().optional(),
    /**
     * Assign the action to a portal identity (a worker or subcontractor with
     * site-portal access) rather than a free-text name.
     *
     * The web route has always supported this through its own local schema;
     * the shared contract did not, so folding web onto the shared inspection
     * service would have silently dropped it. Surfaced by consolidating the
     * two implementations (spec §11.5) — which is the point of consolidating.
     */
    assignedToPortalUserId: z.string().nullable().optional(),
    assignedToName: z.string().max(160).nullable().optional(),
    dueAt: z.string().nullable().optional(),
    photoUrls: z.array(z.string().max(2048)).max(10).default([]),
    safetyCritical: z.boolean().default(false),
  })
  .strict();
export type CorrectiveActionCreateRequest = z.infer<
  typeof CorrectiveActionCreateRequest
>;

export const InspectionCreateRequest = z
  .object({
    title: z.string().min(1).max(180),
    location: z.string().max(160).nullable().optional(),
    inspectedAt: z.string().nullable().optional(),
    status: SiteInspectionStatusSchema.exclude(["ARCHIVED"]).default("OPEN"),
    notes: z.string().max(4000).nullable().optional(),
    items: z.array(InspectionItemInput).max(50).default([]),
    actions: z.array(CorrectiveActionCreateRequest).max(25).default([]),
    /**
     * Plant & Equipment context. The RESPONSE DTO has carried these since the
     * register shipped; the request did not, so both create paths parsed an
     * inspection and dropped its plant linkage before Prisma — which is why no
     * plant-linked inspection could exist, and why INSPECTION requirements had
     * to be refused at the contract boundary (#1996).
     *
     * `plantRequirementId` names the ONE recurring requirement the inspection
     * satisfies. Without it the engine reduced over the asset's whole
     * inspection list, so a weekly pre-use check reset the deadline on an
     * annual thorough examination and the asset read compliant while the
     * examination was overdue (#2000). The server validates ownership and the
     * requirement kind; the schema only carries the ids.
     */
    kind: SiteInspectionKindSchema.default("GENERAL"),
    plantAssetId: z.string().nullable().default(null),
    projectPlantAssignmentId: z.string().nullable().default(null),
    plantRequirementId: z.string().nullable().default(null),
    templateKey: z.string().max(120).nullable().default(null),
  })
  .strict()
  .refine((v) => v.kind === "GENERAL" || Boolean(v.plantAssetId), {
    message: "A plant inspection must identify the item it inspected",
    path: ["plantAssetId"],
  })
  .refine(
    (v) =>
      v.kind !== "GENERAL" ||
      (!v.plantAssetId &&
        !v.projectPlantAssignmentId &&
        !v.plantRequirementId &&
        !v.templateKey),
    {
      // The inverse matters just as much: a GENERAL inspection carrying plant
      // context would be a plant record the compliance engine never counts,
      // because it filters on kind. Silent non-counting is worse than a
      // rejection.
      //
      // EVERY plant field, not just plantAssetId. The service derives isPlant
      // from `kind` alone, so it skips resolvePlantInspectionContext entirely
      // for a GENERAL record — an assignment or requirement id that slipped
      // through would be persisted with no ownership check at all, which is a
      // cross-tenant relation, not merely an uncounted one.
      message: "A general inspection must not carry plant context",
      path: ["kind"],
    },
  )
  .refine((v) => !v.plantAssetId || Boolean(v.projectPlantAssignmentId), {
    // First release: the inspection engine is project-scoped, so a plant
    // inspection is always tied to the placement it happened on (spec §11.1).
    message:
      "A plant inspection must name the project placement it was carried out on",
    path: ["projectPlantAssignmentId"],
  });
export type InspectionCreateRequest = z.infer<typeof InspectionCreateRequest>;
/**
 * What a CLIENT may send — the pre-parse shape, where defaulted fields are
 * optional.
 *
 * `z.infer` is the OUTPUT type: every `.default()` field is required there,
 * because after parsing it always has a value. Typing a client argument
 * against it means adding any new defaulted field instantly breaks every
 * caller that legitimately omits it — which is exactly what adding the plant
 * context did to the mobile app's inspection screen, on two independent
 * branches at once (#2000 and the §11.5 service work).
 *
 * Callers that BUILD a request should use this; callers that CONSUME a parsed
 * one should keep `InspectionCreateRequest`.
 */
export type InspectionCreateInput = z.input<typeof InspectionCreateRequest>;

/**
 * `expectedUpdatedAt` is the OPTIONAL optimistic-concurrency claim (#2369),
 * matched against the row the descriptive fields are editing. The status and
 * `inspectedAt` legs keep their own convergent claims and are untouched by
 * this key. A claim, never a written field: the route strips it before the
 * patch. Absent → the PATCH behaves exactly as before.
 */
export const InspectionUpdateRequest = z
  .object({
    title: z.string().min(1).max(180).optional(),
    location: z.string().max(160).nullable().optional(),
    inspectedAt: z.string().nullable().optional(),
    status: SiteInspectionStatusSchema.optional(),
    notes: z.string().max(4000).nullable().optional(),
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .strict();
export type InspectionUpdateRequest = z.infer<typeof InspectionUpdateRequest>;

/**
 * `expectedUpdatedAt` is the OPTIONAL optimistic-concurrency claim (#2369):
 * matched against the corrective-action row before ANY leg of this PATCH
 * applies (descriptive or status). Absent → today's behaviour. A claim, never
 * a written field.
 */
export const CorrectiveActionUpdateRequest = z
  .object({
    title: z.string().min(1).max(180).optional(),
    description: z.string().max(2000).nullable().optional(),
    status: CorrectiveActionStatusSchema.optional(),
    assignedToPortalUserId: z.string().nullable().optional(),
    assignedToName: z.string().max(160).nullable().optional(),
    dueAt: z.string().nullable().optional(),
    evidenceNotes: z.string().trim().max(2000).nullable().optional(),
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.status !== "CANCELLED" || Boolean(value.evidenceNotes?.trim()),
    {
      message: "A reason is required when cancelling a corrective action",
      path: ["evidenceNotes"],
    },
  );
export type CorrectiveActionUpdateRequest = z.infer<
  typeof CorrectiveActionUpdateRequest
>;
